import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "redux";
import {
  getRefreshToken,
  getUserId,
  removeRefreshToken,
  removeToken,
  removeUserId,
  setToken,
} from "../utils";
import { jwtDecode } from "jwt-decode";

import apiServices from "../service";
import { setPreferencesFromProfile } from "./preferenceSlice";

type userType = {
  [x: string]: any;
};

const initialState = {
  userToken: localStorage.getItem("assystUserToken") || "",
  loading: false,
  userData: <any>{},
  userProfileInfo: <any>{},
  userId: localStorage.getItem("assystUserId") || "",
  refreshToken: localStorage.getItem("assystUserRefreshToken") || "",
};

export const authSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    signOut: (state) => {
      state.loading = false;
      state.userData = {};
      state.userToken = "";
      state.userId = "";
      state.userProfileInfo = {};
    },
    loginSuccess: (state, action) => {
      const { accessToken, user, refreshToken, userId } = action.payload;
      state.userToken = accessToken;
      state.userData = user;
      state.userId = userId;
      state.loading = false;
      state.refreshToken = refreshToken;
    },
    loginFailure: (state) => {
      state.loading = false;
      state.userData = {};
      state.userToken = "";
      state.userId = "";
    },
    fetchUserDataSuccess: (state, action) => {
      const { accessToken, user } = action.payload;
      state.userToken = accessToken;
      state.userData = user;
      state.loading = false;
      state.userId = "";
    },
    fetchUserDataFailure: (state) => {
      state.loading = false;
      state.userData = {};
      state.userToken = "";
      state.userId = "";
    },
    fetchUserProfileData: (state, action) => {
      state.userProfileInfo = action.payload;
    },
  },
});

export const {
  signOut,
  loginSuccess,
  loginFailure,
  fetchUserDataSuccess,
  fetchUserDataFailure,
  fetchUserProfileData,
} = authSlice.actions;

export default authSlice.reducer;

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const doUserLogin = (credentials: userType) => {
  return async (dispatch: Dispatch) => {
    apiServices.auth
      .login(credentials)
      .then((response) => {
        if (response.data.data) {
          const accessToken = response.data.data[0].access_token;
          setToken(accessToken);
          const decodedToken: any = jwtDecode(accessToken);
          dispatch(
            loginSuccess({
              accessToken,
              user: { ...decodedToken },
            })
          );
        }
      })
      .catch((err) => {
        console.log("Login error:", err);
      });
  };
};

export const doLogout = () => {
  return async (dispatch: Dispatch) => {
    dispatch(signOut());
    removeToken();
    removeRefreshToken();
    removeUserId();
  };
};

export const isTokenValid = (token: any): boolean => {
  const decodedToken: { exp: number } = jwtDecode(token);
  return decodedToken
    ? decodedToken.exp * 1000 > new Date().getTime()
    : false;
};

export const setCurrentUser = (token: any) => {
  return async (dispatch: Dispatch) => {
    setToken(token);
    const decodedToken: any = jwtDecode(token);
    dispatch(
      loginSuccess({
        accessToken: token,
        user: { ...decodedToken },
        userId: getUserId(),
        refreshToken: getRefreshToken(),
      })
    );
  };
};

/**
 * Fetches the user profile and simultaneously bootstraps preferences —
 * replaces the separate `fetchUserPreferences` thunk (same API endpoint).
 *
 * Flow:
 *  1. Calls GET /user/profile/:userId  (one request)
 *  2. Dispatches fetchUserProfileData  → updates authSlice.userProfileInfo
 *  3. Dispatches setPreferencesFromProfile → hydrates preferenceSlice table
 *     states from response.data.user_settings.tables
 */
export const getUserProfileInfo = (userId: any) => {
  return async (dispatch: Dispatch) => {
    const fallback = {};

    if (!userId || userId === "") {
      dispatch(fetchUserProfileData(fallback));
      dispatch(setPreferencesFromProfile(fallback));
      return;
    }

    try {
      const response = await apiServices.userProfile.getDetail(userId);
      const profileData = response.data?.data ?? null;

      if (profileData) {
        // Populate auth profile state
        dispatch(fetchUserProfileData(profileData));
        // Hydrate preferences + table states from user_settings.tables
        dispatch(setPreferencesFromProfile(profileData));
      } else {
        dispatch(fetchUserProfileData(fallback));
        dispatch(setPreferencesFromProfile(fallback));
      }
    } catch (err) {
      console.error("getUserProfileInfo error:", err);
      dispatch(fetchUserProfileData(fallback));
      dispatch(setPreferencesFromProfile(fallback));
    }
  };
};